// src/hooks/useResumoOnboarding.ts
// =====================================================================
// PROGRESSIVE ONBOARDING — Modo Interagir das Apostilas
// =====================================================================

import { useState, useEffect, useCallback, useRef } from 'react';

export type ResumoOnboardingStep =
    | 'interact-button'    // Step 1: botão Interagir no header
    | 'highlight-activate' // Step 2: botão Ativar na toolbar
    | 'highlight-comment'  // Step 3: clicar no grifo para comentar (só dispara se houver grifo de verdade)
    | 'notes'              // Step 4: categorias de anotação
    | 'comments-mode'      // Step 5: modo Comentário (seleciona texto solto, painel lateral)
    | 'notes-sidebar'      // Step 6: botão flutuante da sidebar de notas
    | 'done';

interface ResumoOnboardingState {
    dismissed: boolean;
    completedSteps: ResumoOnboardingStep[];
}

// Seletor do elemento-alvo de cada step cuja posição é calculada a partir do
// DOM de verdade (data-tutorial-target na toolbar), em vez de pixels fixos
// chutados — foi exatamente um valor chutado que ficou desatualizado e passou
// a apontar pro Desfazer/Refazer quando esses botões entraram na toolbar.
const ANCHOR_SELECTORS: Partial<Record<ResumoOnboardingStep, string>> = {
    'highlight-activate': '[data-tutorial-target="highlight-activate"]',
    'notes': '[data-tutorial-target="notes-categories"]',
    'comments-mode': '[data-tutorial-target="comments-toggle"]',
};

const STORAGE_KEY = 'graia_resumo_onboarding';
const FALLBACK_DELAY_MS = 15000;

export function useResumoOnboarding() {
    const [currentStep, setCurrentStep] = useState<ResumoOnboardingStep | null>(null);
    const [isVisible, setIsVisible] = useState(false);

    // Posição dinâmica para o step 3 (próximo ao grifo feito pelo usuário)
    const [highlightCommentPosition, setHighlightCommentPosition] = useState<{
        top: number;
        left: number;
    } | null>(null);

    // Posição dinâmica genérica pros steps ancorados num botão real da toolbar
    // (ver ANCHOR_SELECTORS) — width incluída pra desenhar a "chave" do step 4.
    const [anchorPosition, setAnchorPosition] = useState<{
        top: number;
        left: number;
        width: number;
    } | null>(null);

    const highlightFallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const notesFallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ===== PERSISTÊNCIA =====
    const loadState = useCallback((): ResumoOnboardingState => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return { dismissed: false, completedSteps: [] };
            return JSON.parse(raw);
        } catch {
            return { dismissed: false, completedSteps: [] };
        }
    }, []);

    const saveState = useCallback((patch: Partial<ResumoOnboardingState>) => {
        try {
            const current = loadState();
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...patch }));
        } catch { }
    }, [loadState]);

    const hasCompleted = useCallback((step: ResumoOnboardingStep): boolean => {
        const state = loadState();
        return state.dismissed || state.completedSteps.includes(step);
    }, [loadState]);

    const markCompleted = useCallback((step: ResumoOnboardingStep) => {
        const state = loadState();
        if (!state.completedSteps.includes(step)) {
            saveState({ completedSteps: [...state.completedSteps, step] });
        }
    }, [loadState, saveState]);

    const clearFallbackTimers = useCallback(() => {
        if (highlightFallbackTimer.current) {
            clearTimeout(highlightFallbackTimer.current);
            highlightFallbackTimer.current = null;
        }
        if (notesFallbackTimer.current) {
            clearTimeout(notesFallbackTimer.current);
            notesFallbackTimer.current = null;
        }
    }, []);

    useEffect(() => {
        return () => clearFallbackTimers();
    }, [clearFallbackTimers]);

    // ===== Mede a posição do botão-alvo real na toolbar sempre que um step
    // ancorado no DOM fica visível (ver ANCHOR_SELECTORS) =====
    useEffect(() => {
        if (!currentStep || !isVisible) return;
        const selector = ANCHOR_SELECTORS[currentStep];
        if (!selector) return;

        // Pequeno atraso pra garantir que a toolbar já terminou de renderizar
        const t = setTimeout(() => {
            const el = document.querySelector(selector);
            if (!el) {
                setAnchorPosition(null);
                return;
            }
            const rect = el.getBoundingClientRect();
            setAnchorPosition({
                top: rect.bottom + 10,
                left: rect.left + rect.width / 2,
                width: rect.width,
            });
        }, 100);

        return () => clearTimeout(t);
    }, [currentStep, isVisible]);

    // ===== STEP 1: ao montar =====
    useEffect(() => {
        const state = loadState();
        if (state.dismissed) return;
        if (state.completedSteps.includes('interact-button')) return;

        const timer = setTimeout(() => {
            setCurrentStep('interact-button');
            setIsVisible(true);
        }, 1000);

        return () => clearTimeout(timer);
    }, [loadState]);

    // ===== STEP 2: entrou no modo Interagir =====
    const onInteractModeEntered = useCallback(() => {
        const state = loadState();
        if (state.dismissed) return;
        if (hasCompleted('highlight-activate')) return;

        setIsVisible(false);
        markCompleted('interact-button');

        setTimeout(() => {
            setCurrentStep('highlight-activate');
            setIsVisible(true);
        }, 400);
    }, [loadState, hasCompleted, markCompleted]);

    // ===== Fallback do step 2: se o usuário não grifar nada, pula direto pro
    // step de anotações — não faz sentido explicar "clique num grifo" quando
    // nenhum grifo existe. O step de comentar grifo só aparece de verdade via
    // onFirstHighlightMade, no primeiro grifo real, seja quando for. =====
    const advanceToNotesFallback = useCallback(() => {
        const state = loadState();
        if (state.dismissed) return;
        if (hasCompleted('notes')) return;

        setIsVisible(false);
        markCompleted('highlight-activate');

        setTimeout(() => {
            setCurrentStep('notes');
            setIsVisible(true);
        }, 1200);
    }, [loadState, hasCompleted, markCompleted]);

    // ===== Agenda o fallback acima enquanto o step 2 estiver visível =====
    useEffect(() => {
        if (currentStep === 'highlight-activate' && isVisible) {
            if (hasCompleted('notes')) return;

            highlightFallbackTimer.current = setTimeout(() => {
                advanceToNotesFallback();
            }, FALLBACK_DELAY_MS);

            return () => {
                if (highlightFallbackTimer.current) {
                    clearTimeout(highlightFallbackTimer.current);
                    highlightFallbackTimer.current = null;
                }
            };
        }
    }, [currentStep, isVisible, hasCompleted, advanceToNotesFallback]);

    // ===== STEP 3 (via ação): 1º grifo feito =====
    // highlightEl: o elemento <mark> do grifo no DOM (para posicionar o tooltip perto)
    const onFirstHighlightMade = useCallback((highlightId?: string) => {
        const state = loadState();
        if (state.dismissed) return;
        if (hasCompleted('highlight-comment')) return;

        // Cancela o fallback "pular pra notas" — o usuário grifou de verdade,
        // então o tutorial de comentar grifo faz sentido agora.
        if (highlightFallbackTimer.current) {
            clearTimeout(highlightFallbackTimer.current);
            highlightFallbackTimer.current = null;
        }

        // Tentar posicionar o tooltip perto do grifo recém-feito
        let pos = { top: window.innerHeight - 220, left: window.innerWidth / 2 - 130 };

        if (highlightId) {
            // Aguarda o DOM renderizar o grifo antes de medir
            setTimeout(() => {
                const el = document.querySelector(`[data-highlight-id="${highlightId}"]`);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    const tooltipWidth = 260;
                    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
                    let top = rect.bottom + 12; // abaixo do grifo

                    // Garantir que não sai da tela
                    left = Math.max(8, Math.min(left, window.innerWidth - tooltipWidth - 8));
                    if (top + 200 > window.innerHeight) {
                        top = rect.top - 200 - 12; // acima se não couber embaixo
                    }

                    setHighlightCommentPosition({ top, left });
                } else {
                    setHighlightCommentPosition(pos);
                }

                setIsVisible(false);
                markCompleted('highlight-activate');
                setTimeout(() => {
                    setCurrentStep('highlight-comment');
                    setIsVisible(true);
                }, 1200);
            }, 150); // aguarda re-render do grifo

            return; // retorno antecipado, o setTimeout acima toma conta
        }

        setHighlightCommentPosition(pos);
        setIsVisible(false);
        markCompleted('highlight-activate');

        setTimeout(() => {
            setCurrentStep('highlight-comment');
            setIsVisible(true);
        }, 500);
    }, [loadState, hasCompleted, markCompleted]);

    // ===== Agenda fallback step 4 quando step 3 fica visível =====
    useEffect(() => {
        if (currentStep === 'highlight-comment' && isVisible) {
            if (hasCompleted('notes')) return;

            notesFallbackTimer.current = setTimeout(() => {
                const state = loadState();
                if (state.dismissed || hasCompleted('notes')) return;

                markCompleted('highlight-comment');
                setIsVisible(false);
                setTimeout(() => {
                    setCurrentStep('notes');
                    setIsVisible(true);
                }, 300);
            }, FALLBACK_DELAY_MS);

            return () => {
                if (notesFallbackTimer.current) {
                    clearTimeout(notesFallbackTimer.current);
                    notesFallbackTimer.current = null;
                }
            };
        }
    }, [currentStep, isVisible, hasCompleted, markCompleted, loadState]);

    // ===== STEP 4 (via ação): 1ª nota criada =====
    const onFirstNoteMade = useCallback(() => {
        const state = loadState();
        if (state.dismissed) return;
        if (hasCompleted('notes')) return;

        if (notesFallbackTimer.current) {
            clearTimeout(notesFallbackTimer.current);
            notesFallbackTimer.current = null;
        }

        setIsVisible(false);
        markCompleted('highlight-comment');

        setTimeout(() => {
            setCurrentStep('notes');
            setIsVisible(true);
        }, 500);
    }, [loadState, hasCompleted, markCompleted]);

    // ===== STEP 6: saiu do modo Interagir =====
    const onInteractModeClosed = useCallback(() => {
        const state = loadState();
        if (state.dismissed) return;
        if (hasCompleted('notes-sidebar')) return;

        clearFallbackTimers();
        setIsVisible(false);
        markCompleted('notes');
        markCompleted('comments-mode');

        setTimeout(() => {
            setCurrentStep('notes-sidebar');
            setIsVisible(true);
        }, 600);
    }, [loadState, hasCompleted, markCompleted, clearFallbackTimers]);

    // ===== FECHAR TOOLTIP =====
    const dismissStep = useCallback((step: ResumoOnboardingStep, permanent: boolean) => {
        setIsVisible(false);
        markCompleted(step);

        if (permanent) {
            saveState({ dismissed: true });
            clearFallbackTimers();
            setTimeout(() => setCurrentStep(null), 300);
            return;
        }

        // Ao apertar Ok, avança para o próximo step automaticamente após um tempo
        setTimeout(() => {
            setCurrentStep(null);

            // Fechou o step 2 (ativar grifo): agenda o fallback pra "notas" —
            // se o usuário grifar antes disso, onFirstHighlightMade cancela
            // esse timer e mostra o comentar-grifo de verdade no lugar.
            if (step === 'highlight-activate') {
                highlightFallbackTimer.current = setTimeout(() => {
                    const state = loadState();
                    if (state.dismissed || state.completedSteps.includes('notes')) return;
                    setCurrentStep('notes');
                    setIsVisible(true);
                }, FALLBACK_DELAY_MS);
            }

            // Fechou o step 3 (comentar grifo): agenda o step 4 (notas)
            if (step === 'highlight-comment') {
                notesFallbackTimer.current = setTimeout(() => {
                    const state = loadState();
                    if (state.dismissed || state.completedSteps.includes('notes')) return;
                    setCurrentStep('notes');
                    setIsVisible(true);
                }, FALLBACK_DELAY_MS);
            }

            // Fechou o step 4 (notas): avança pro step 5 (modo Comentário),
            // ainda dentro do modo Interagir — a sidebar de notas (step 6) só
            // faz sentido depois que o usuário sair do modo, e é disparada por
            // onInteractModeClosed.
            if (step === 'notes') {
                const state = loadState();
                if (!state.dismissed && !state.completedSteps.includes('comments-mode')) {
                    setTimeout(() => {
                        setCurrentStep('comments-mode');
                        setIsVisible(true);
                    }, 800);
                }
            }
        }, 300);
    }, [markCompleted, saveState, clearFallbackTimers, loadState]);

    // ===== RESET =====
    const resetOnboarding = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        clearFallbackTimers();
        setCurrentStep(null);
        setIsVisible(false);
        setHighlightCommentPosition(null);
        setAnchorPosition(null);
    }, [clearFallbackTimers]);

    return {
        currentStep,
        isVisible,
        highlightCommentPosition,
        anchorPosition,
        onInteractModeEntered,
        onFirstHighlightMade,
        onFirstNoteMade,
        onInteractModeClosed,
        dismissStep,
        resetOnboarding,
        hasCompleted,
    };
}
